import { InProductionTourSteps } from "../../components/TourGuide/tourGuideData";
import { useEffect, useRef, useState } from "react";
import Loader from "../../common/Loader";
import {
  dieMasterAPI,
  DieUnloadReasonAPI,
  inProduction,
} from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import { RegistryHeader, CalculationFooter } from "../../common/Shared";
import {
  Info,
  LayoutGrid,
  RefreshCw,
} from "lucide-react";
import { STATUS_COLORS } from "../../utlis/constant";
import { AnimatePresence, motion } from "framer-motion";
import { ProductionCard } from "./ProductionCard";
import Table from "../../common/Table";
import Model from "../../common/Model";
import { TourGuide } from "../../components/TourGuide/TourGuide";

const InProduction = () => {
  const getDiePdfUrl = (row) => {
    if (!row) return "";
    const pdfUrl = row.die_Pdf || row.diePdf || row.Die_Pdf;
    if (pdfUrl) return pdfUrl;

    const dieId = row.die_Id || row.dieId || row.Die_Id;
    if (dieId) {
      return `https://hline.blob.core.windows.net/hlinefiles/Extrusion/DieMedia/Pdf/${dieId}.pdf`;
    }
    return "";
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [inProductionData, setInProductionData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCastingData, setIsCastingData] = useState([]);
  const [isMarkDoneData, setIsMarkDoneData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [activeTab, setActiveTab] = useState("in-ProductionData");
  const [selectedDies, setSelectedDies] = useState([]);
  const [dieList, setDieList] = useState([]);
  const [dieUnloadReason, setDieUnloadReason] = useState([]);
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  const tableScrollRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const [isBilletModelOpen, setIsBilletModelOpen] = useState(false);
  const [billetModalRow, setBilletModalRow] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);

  const handleTourComplete = () => {
    localStorage.setItem("aluex_inproduction_tour_seen", "true");
  };

  const toggleDieSelection = (id) => {
    setSelectedDies((prev) => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        const updatedSelection = prev.filter((x) => x !== id);
        setInProductionData((prevData) =>
          prevData.filter((row) => row.die_Id !== id),
        );
        return updatedSelection;
      } else {
        return [...prev, id];
      }
    });
  };

  const fetchMarkDoneData = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await inProduction.getAllInMarkDone(currentPage, pageSize);

      const list = res?.data || [];

      if (currentPage === 1) {
        setIsMarkDoneData(list);
      } else {
        setIsMarkDoneData((prev) => [...prev, ...list]);
      }

      if (list.length < pageSize) {
        setHasMore(false);
      }
    } catch (error) {
      notify("Error fetching Casting data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [castingNoRes, ReasonRes] = await Promise.all([
        inProduction.getAllCastingNo(),
        DieUnloadReasonAPI.getAll(),
      ]);
      setIsCastingData(
        castingNoRes.map((cas) => ({
          value: cas.casting_No,
          label: cas.casting_No,
        })),
      );
      setDieUnloadReason(
        ReasonRes.data.map((cas) => ({
          value: cas.id,
          label: cas.reason,
        })),
      );
    } catch (error) {
      notify("Error fetching location data", "error");
    }
    setLoading(false);
  };

  const fetchInProductionData = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const res = await inProduction.getAllInProduction(
        selectedDies,
        currentPage,
        pageSize,
      );

      const list = res?.data || [];

      if (currentPage === 1) {
        setInProductionData(list);
      } else {
        setInProductionData((prev) => [...prev, ...list]);
      }

      if (list.length < pageSize) {
        setHasMore(false);
      }
    } catch (err) {
      notify("Error fetching Order Plan data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchDies = async () => {
    try {
      const res = await dieMasterAPI.getAllDieForBatches();
      setDieList(
        res.map((d) => ({
          die_Id: d.die_Id,
          dieNumber: d.dieNumber,
          die_Status_Id: d.die_Status_Id,
          die_Status: d.die_Status,
          currentWeight: d.currentWeight,
          cutLength: d.cut_Length,
          orderPcs: d.order_PCS,
          orderPcsWeight: d.order_Weight,
          soNo: d.order_No,
          batchId: d.batch_Id,
        })),
      );
    } catch (err) {
      console.log("Error loading die list", err);
    }
  };

  const handleRefresh = () => {
    if (selectedDies.length === 0) {
      setInProductionData([]);
      return;
    }
    setCurrentPage(1);
    fetchInProductionData();
    setIsTableExpanded(false);
  };

  useEffect(() => {
    fetchData();
    fetchDies();
  }, []);

  useEffect(() => {
    if (selectedDies.length > 0) {
      fetchInProductionData();
    }
  }, [currentPage]);

  useEffect(() => {
    if (activeTab === "finish") {
      fetchMarkDoneData(1);
    }
  }, [activeTab]);

  const calculateRowValues = (row) => {
    const billetSize = parseFloat(row.billet_Size) || 0;
    const noOfBillet = parseFloat(row.no_Of_Billet) || 0;
    const outWt = parseFloat(row.output_Weight) || 0;
    const inputWt = 0.0386 * billetSize * noOfBillet;

    if (!isNaN(inputWt)) {
      row.input_Weight = parseFloat(inputWt.toFixed(2));
    }

    if (row.input_Weight > 0 && outWt > 0) {
      const recovery = (outWt / row.input_Weight) * 100;
      row.recovry = parseFloat(recovery.toFixed(2));
    } else {
      row.recovry = 0;
    }
    return row;
  };

  const handleRowChange = (index, field, value) => {
    const updated = [...inProductionData];
    let row = updated[index];

    row[field] = value;
    if (row.isCloned) {
      row.isCloned = false;
    }

    row = calculateRowValues(row);
    setInProductionData(updated);
  };

  const handleModalRowChange = (field, value) => {
    setBilletModalRow((prev) => {
      if (!prev) return prev;
      let updatedNew = { ...prev.new, [field]: value };
      updatedNew = calculateRowValues(updatedNew);
      return { ...prev, new: updatedNew };
    });
  };

  const handleSave = async (row, index, opts = {}) => {
    setIsSaving(true);
    const { skipRefresh = false } = opts;
    if (!row.casting_No) {
      setIsSaving(false);
      return notify("Casting No required!", "error");
    }
    if (!row.billet_Size) {
      setIsSaving(false);
      return notify("Billet Size required!", "error");
    }
    if (!row.no_Of_Billet) {
      setIsSaving(false);
      return notify("No of billets required!", "error");
    }
    const payload = {
      seq_No: row.seq_No,
      die_Id: row.die_Id,
      order_No: row.order_No,
      batch_Id: row.batch_Id,
      order_Detail_Id: row.order_Detail_Id,
      order_Qty_Pcs: parseInt(row.qty_Pcs) || 0,
      order_Qty_Weight: parseFloat(row.qty_Weight) || 0,
      casting_No: row.casting_No?.value || row.casting_No,
      Cut_Length: parseFloat(row.cut_Length) || 0,
      pcs_Needed: parseFloat(row.pcs_Needed) || 0,
      stertching_Rejection_Ft: parseFloat(row.stertching_Rejection_Ft) || 0,
      but_End_Rejection_Mm: parseFloat(row.but_End_Rejection_Mm) || 0,
      die_In_Time: row.die_In,
      die_Out_Time: row.die_Out,
      act_Output_WT: parseFloat(row.output_Weight) || 0,
      act_Input_WT: parseFloat(row.input_Weight) || 0,
      rec_Billet_Length: parseFloat(row.billet_Size) || 0,
      rec_Billet_PCS: parseInt(row.no_Of_Billet) || 0,
      act_production_Qty: parseInt(row.no_Of_Pcs) || 0,
      act_Yield_Per: parseFloat(row.recovry) || 0,
      extrusion_Speed: parseFloat(row.ext_Speed) || 0,
      billet_Temp: parseFloat(row.billet_Temp) || 0,
      extrusion_Pressure: parseFloat(row.extrusion_Pressure) || 0,
      remark: row.remark || "",
      created_By: userId,
    };

    try {
      await inProduction.saveOrUpdate(payload);
      if (!skipRefresh) {
        fetchInProductionData();
      }
      notify(`Row ${index + 1} saved successfully!`, "success");
    } catch (error) {
      notify(`Failed to save Row ${index + 1}`, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const buildBilletPayload = (row) => {
    const payload = {
      currentSeqNo: row.seq_No || 0,
      batchId: row.batch_Id,
      orderDetailId: row.order_Detail_Id,
      castingNo: row.casting_No?.value || row.casting_No,
      dieInTime: row.die_In,
      dieOutTime: row.die_Out,
      actOutputWt: parseFloat(row.output_Weight) || 0,
      actInputWt: parseFloat(row.input_Weight) || 0,
      recBilletLength: parseFloat(row.billet_Size) || 0,
      recBilletPcs: parseInt(row.no_Of_Billet) || 0,
      actButtEnd: parseFloat(row.but_End_Rejection_Mm) || 0,
      actProductionQty: parseInt(row.no_Of_Pcs) || 0,
      actYieldPer: parseFloat(row.recovry) || 0,
      extrusionSpeed: parseFloat(row.ext_Speed) || 0,
      billetTemp: parseFloat(row.billet_Temp) || 0,
      extrusionPressure: parseFloat(row.extrusion_Pressure) || 0,
      remark: row.remark || "",
      butEndRejectionMm: parseFloat(row.but_End_Rejection_Mm) || 0,
      stretchingRejectionFt: parseFloat(row.stertching_Rejection_Ft) || 0,
      pcsNeeded: parseFloat(row.pcs_Needed) || 0,
      userId: userId,
    };

    return payload;
  };

  const handleSaveBilletChange = async () => {
    if (!billetModalRow?.old || !billetModalRow?.new) return;
    setIsSaving(true);

    const oldRow = { ...billetModalRow.old };
    const newRow = { ...billetModalRow.new };

    if (!oldRow.casting_No) {
      setIsSaving(false);
      return notify("Old Row Casting No required!", "error");
    }
    if (!oldRow.billet_Size) {
      setIsSaving(false);
      return notify("Old Row Billet Size required!", "error");
    }
    if (!oldRow.no_Of_Billet) {
      setIsSaving(false);
      return notify("Old Row No of billets required!", "error");
    }

    if (!newRow.casting_No) {
      setIsSaving(false);
      return notify("Casting No required!", "error");
    }
    if (!newRow.billet_Size) {
      setIsSaving(false);
      return notify("Billet Size required!", "error");
    }
    if (!newRow.no_Of_Billet) {
      setIsSaving(false);
      return notify("No of billets required!", "error");
    }

    oldRow.die_Out = billetModalRow.currentTime;
    const oldPayload = buildBilletPayload(oldRow);

    try {
      await inProduction.saveBilletChange(oldPayload);
      await handleSave(newRow, billetModalRow.index, { skipRefresh: true });

      notify("Billet changed successfully!", "success");
      closeBilletModal();
      fetchInProductionData();
      fetchDies();
    } catch (err) {
      notify("Billet change failed!", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkDone = async (index) => {
    setIsSaving(true);
    const row = inProductionData[index];
    const payload = {
      seq_No: row.seq_No,
      order_No: row.order_No,
      batch_Id: row.batch_Id,
      order_Detail_Id: row.order_Detail_Id,
      die_Id: row.die_Id,
      casting_No: row.casting_No?.value || row.casting_No,
      Cut_Length: parseFloat(row.cut_Length) || 0,
      pcs_Needed: parseFloat(row.pcs_Needed) || 0,
      stertching_Rejection_Ft: parseFloat(row.stertching_Rejection_Ft) || 0,
      but_End_Rejection_Mm: parseFloat(row.but_End_Rejection_Mm) || 0,
      die_In_Time: row.die_In,
      die_Out_Time: row.die_Out,
      act_Output_WT: parseFloat(row.output_Weight) || 0,
      act_Input_WT: parseFloat(row.input_Weight) || 0,
      rec_Billet_Length: parseFloat(row.billet_Size) || 0,
      rec_Billet_PCS: parseInt(row.no_Of_Billet) || 0,
      act_production_Qty: parseInt(row.no_Of_Pcs) || 0,
      act_Yield_Per: parseFloat(row.recovry) || 0,
      extrusion_Speed: parseFloat(row.ext_Speed) || 0,
      billet_Temp: parseFloat(row.billet_Temp) || 0,
      extrusion_Pressure: parseFloat(row.extrusion_Pressure) || 0,
      remark: row.remark || "",
      created_By: userId,
    };

    try {
      await inProduction.saveOrUpdate(payload);
      notify(
        `Row ${index + 1} saved successfully before marking done!`,
        "success",
      );
      await inProduction.MarkProductionDone({
        batch_Id: row.batch_Id,
        seq_No: row.seq_No || 0,
        created_By: userId,
      });

      notify(`Row ${index + 1} marked as done!`, "success");
      fetchInProductionData();
      fetchDies();
    } catch (error) {
      notify(`Failed to save or mark row ${index + 1} as done`, "error");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const outputWtTotal = isMarkDoneData.reduce(
    (sum, row) => sum + (parseFloat(row.output_Weight) || 0),
    0,
  );

  const pcsTotal = isMarkDoneData.reduce(
    (sum, row) => sum + (parseFloat(row.no_Of_Pcs) || 0),
    0,
  );

  const n = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };

  const safeDiv = (a, b) => {
    const A = n(a),
      B = n(b);
    return B > 0 ? A / B : null;
  };

  const calcFinal = (r) => {
    const currentDiePer12 = n(r.current_Die_Weight);
    const cutLenFt = n(r.cut_Length);
    const pcsNeeded = n(r.pcs_Needed);
    const butEndMm = n(r.but_End_Rejection_Mm);
    const stretchFt = n(r.stertching_Rejection_Ft);
    const cutLenMM = cutLenFt * 304.8;
    const secondCal = (currentDiePer12 / 3660) * cutLenMM;

    const suggBilletlength =
      secondCal * pcsNeeded +
      butEndMm * 0.0386 +
      (currentDiePer12 / 3660) * stretchFt * 304;

    const final = suggBilletlength / 0.0386;
    return Number.isFinite(final) ? final : null;
  };

  const columns = [
    {
      key: "seq_No",
      label: "#",
      render: (_, row) => (
        <span className="text-slate-400 font-mono text-xs">#{row.seq_No}</span>
      ),
    },
    {
      key: "orderWithCastingNo",
      label: "Order & Casting",
      render: (_, row) => (
        <div className="flex flex-col gap-1.5 items-center">
          <div className="flex items-center gap-1">
            <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100 ">
              ORD: {row.order_No}
            </span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
            <span className="font-semibold text-slate-400">CAST:</span>
            <span className="font-bold text-slate-700">{row.casting_No}</span>
          </div>
        </div>
      ),
    },
    {
      key: "batchwithDienumber",
      label: "Die & Batch",
      render: (_, row) => {
        const pdfUrl = getDiePdfUrl(row);
        return (
          <div className="flex flex-col gap-1 items-center">
            {pdfUrl ? (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-black hover:underline cursor-pointer text-sm tracking-tight"
              >
                {row.dieNumber}
              </a>
            ) : (
              <span className="text-sm font-black text-slate-800 tracking-tight">
                {row.dieNumber}
              </span>
            )}
            <span className="text-[10px] font-mono text-slate-500 bg-slate-50 px-1.5 rounded border border-slate-200">
              {row.batch_Id}
            </span>
          </div>
        );
      },
    },
    {
      key: "orderQtyWithQtyweight",
      label: "Order Metrics",
      render: (_, row) => (
        <div className="flex flex-col gap-1 items-start min-w-[100px] px-2">
          <div className="flex justify-between w-full text-xs">
            <span className="text-slate-400 font-medium">Qty:</span>
            <span className="font-bold text-slate-700">{row.qty_Pcs}</span>
          </div>
          <div className="w-full h-px bg-slate-100 my-0.5"></div>
          <div className="flex justify-between w-full text-xs">
            <span className="text-slate-400 font-medium">Wt:</span>
            <span className="font-bold text-emerald-600">
              {row.qty_Weight}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "cutLength",
      label: "Cut Length",
      render: (_, row) => (
        <div className="flex flex-col items-center gap-1">
          <span className="font-bold text-slate-800 text-sm">
            {row.cut_Length}
          </span>
          <span className="text-[10px] text-slate-400 bg-slate-50 px-1.5 rounded border border-slate-100">
            {row.cut_Length_MM}
          </span>
        </div>
      ),
    },
    {
      key: "profile_Name",
      label: "Profile",
      render: (_, row) => (
        <div className="min-w-[150px] text-center">
          <span className="font-bold text-slate-600 text-xs px-2 py-1 bg-slate-100 rounded-md inline-block whitespace-normal">
            {row.profile_Name}
          </span>
        </div>
      ),
    },
    {
      key: "dieInOut",
      label: "Timeline",
      render: (_, row) => (
        <div className="flex flex-col gap-1 text-[11px] min-w-[80px]">
          <div className="flex justify-between items-center text-emerald-600 bg-emerald-50/50 px-1.5 py-0.5 rounded">
            <span className="font-bold">IN</span>
            <span className="font-mono">{row.die_In}</span>
          </div>
          <div className="flex justify-between items-center text-red-500 bg-red-50/50 px-1.5 py-0.5 rounded">
            <span className="font-bold">OUT</span>
            <span className="font-mono">{row.die_Out}</span>
          </div>
        </div>
      ),
    },
    {
      key: "inputOutputWt",
      label: "Weight Analysis",
      render: (_, row) => (
        <div className="flex flex-col gap-1 items-center min-w-[90px]">
          <div className="grid grid-cols-2 gap-2 text-xs w-full">
            <div className="text-right">
              <span className="block text-[9px] text-slate-400 uppercase font-bold">
                In
              </span>
              <span className="font-bold text-slate-700">
                {row.input_Weight}
              </span>
            </div>
            <div className="text-left border-l pl-2 border-slate-200">
              <span className="block text-[9px] text-emerald-500 uppercase font-bold">
                Out
              </span>
              <span className="font-bold text-emerald-700">
                {row.output_Weight}
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "billet_Size",
      label: "Billet Size",
      render: (_, row) => (
        <span className="font-mono font-bold text-slate-700 bg-amber-50 px-2 py-1 rounded border border-amber-100 text-xs">
          {row.billet_Size}
        </span>
      ),
    },
    {
      key: "billetPcs",
      label: "Billet / Pcs",
      render: (_, row) => (
        <div className="flex flex-col items-center">
          <span className="font-bold text-slate-800">{row.no_Of_Billet}</span>
          <span className="text-[10px] text-slate-400">{row.no_Of_Pcs} pcs</span>
        </div>
      ),
    },
    {
      key: "pcs_Needed",
      label: "Needed",
      render: (_, row) => (
        <span className="font-medium text-slate-500 text-xs">
          {row.pcs_Needed || "-"}
        </span>
      ),
    },
    {
      key: "recovry",
      label: "Recovery",
      render: (_, row) => (
        <div className="flex items-center gap-1 justify-center">
          <div className="relative w-10 h-10 flex items-center justify-center">
            {/* Circular Progress would go here, using simple text for now */}
            <span
              className={`font-black text-xs ${row.recovry >= 90
                ? "text-emerald-600"
                : row.recovry >= 80
                  ? "text-blue-600"
                  : "text-amber-600"
                }`}
            >
              {row.recovry}%
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "billet_Temp",
      label: "Temp",
      render: (_, row) => (
        <span className="text-xs font-medium text-slate-600">
          {row.billet_Temp}°
        </span>
      ),
    },
    {
      key: "speedPressure",
      label: "Speed / Press",
      render: (_, row) => (
        <div className="flex flex-col text-center text-xs">
          <span className="font-bold text-slate-700">{row.ext_Speed}</span>
          <span className="text-[10px] text-slate-400">
            {row.extrusion_Pressure}
          </span>
        </div>
      ),
    },
    { key: "current_Die_Weight", label: "Die Wt" },
    {
      key: "but_End_Rejection_Mm",
      label: "Rejection",
      render: (_, row) => (
        <div className="flex flex-col gap-1 text-[10px]">
          <div className="flex justify-between gap-2">
            <span className="text-slate-400">Butt:</span>
            <span className="font-medium text-rose-600">
              {row.but_End_Rejection_Mm}mm
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-400">Str:</span>
            <span className="font-medium text-rose-600">
              {row.stertching_Rejection_Ft}ft
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "created_Date",
      label: "Date",
      render: (_, row) => (
        <div className="flex flex-col text-[10px] text-slate-500">
          <span className="font-bold text-slate-700">
            {new Date(row.created_Date).toLocaleDateString()}
          </span>
          <span>{new Date(row.created_Date).toLocaleTimeString()}</span>
        </div>
      ),
    },
    {
      key: "remark",
      label: "Remark",
      render: (_, row) =>
        row.remark ? (
          <div className="group relative">
            <Info size={16} className="text-slate-300 hover:text-blue-500 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 w-48 p-2 bg-slate-800 text-white text-xs rounded-lg  opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              {row.remark}
              <div className="absolute top-full right-2 border-4 border-transparent border-t-slate-800"></div>
            </div>
          </div>
        ) : (
          <span className="text-slate-200">-</span>
        ),
    },
  ];

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (activeTab !== "finish" && activeTab !== "in-ProductionData") return;

      if (loading || !hasMore || isFetchingRef.current) return;
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore, activeTab]);

  const handleFinishRefresh = async () => {
    setIsMarkDoneData([]);
    setHasMore(true);
    setCurrentPage(1);

    const el = tableScrollRef.current?.querySelector(".overflow-y-auto");
    if (el) el.scrollTop = 0;

    await fetchMarkDoneData(1);
    notify("Data loaded (first 50)", "success");
  };

  const closeBilletModal = () => {
    setIsBilletModelOpen(false);

    setBilletModalRow(null);

    setInProductionData((prev) => {
      const updated = [...prev];
      if (billetModalRow) {
        updated[billetModalRow.index].selectedAction = "";
      }
      return updated;
    });
  };

  const handleActionChange = async (index, value) => {
    const updatedData = [...inProductionData];

    updatedData[index].selectedAction = value;
    setInProductionData([...updatedData]);

    if (value === "billet_change") {
      const currentRow = updatedData[index];
      const currentTime = new Date().toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      const firstBilletSize =
        isCastingData.length > 0 ? isCastingData[0].value : "";

      const newRow = {
        die_Id: currentRow.die_Id,
        order_No: currentRow.order_No,
        batch_Id: currentRow.batch_Id,
        profile_Name: currentRow.profile_Name,
        dieNumber: currentRow.dieNumber,
        qty_Pcs: currentRow.qty_Pcs,
        qty_Weight: currentRow.qty_Weight,
        cut_Length: currentRow.cut_Length,
        order_Detail_Id: currentRow.order_Detail_Id,
        current_Die_Weight: currentRow.current_Die_Weight,
        seq_No: 0,
        casting_No: "",
        die_In: currentTime,
        die_Out: "",
        pcs_Needed: "",
        billet_Size: "",
        no_Of_Billet: "",
        no_Of_Pcs: "",
        output_Weight: 0,
        input_Weight: 0,
        recovry: 0,
        but_End_Rejection_Mm: "",
        stertching_Rejection_Ft: "",
        ext_Speed: "",
        billet_Temp: "",
        remark: "",
        die_Unload_Reason_Id: "",
        selectedAction: "",
        isCloned: true,
      };

      setBilletModalRow({
        old: { ...currentRow },
        new: newRow,
        index,
        currentTime,
      });

      setIsBilletModelOpen(true);
    }

    if (value === "mark_done") {
      await handleMarkDone(index);
      return;
    }
    if (value === "die_unload") {
      if (!updatedData[index].die_Unload_Reason_Id)
        return notify("Select unload reason first!", "error");

      updatedData[index].selectedAction = "die_unload";
      setInProductionData([...updatedData]);
      return;
    }

    setInProductionData(updatedData);
  };

  const handleProdStart = async (row, index) => {
    setIsSaving(true);
    try {
      if (!row.casting_No) return notify("Casting No required!", "error");
      if (!row.billet_Size) return notify("Billet Size required!", "error");
      if (!row.no_Of_Billet) return notify("No of billets required!", "error");

      const now = new Date().toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      const updated = [...inProductionData];
      updated[index].die_In = now;
      setInProductionData(updated);

      await handleSave(updated[index], index, { skipRefresh: true });

      const res = await inProduction.ProdStart({
        dieId: updated[index].die_Id,
        orderDetailId: updated[index].order_Detail_Id,
      });

      if (res?.status === true) {
        notify(res?.message || "Success", "success");
      }

      fetchInProductionData();
      fetchDies();
    } catch (e) {
      notify("Prod start failed", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDieUnload = async (row, index) => {
    setIsSaving(true);
    if (!row.die_Unload_Reason_Id) {
      setIsSaving(false);
      return notify("Unload reason required!", "error");
    }

    const payload = {
      dieId: row.die_Id,
      orderDetailId: row.order_Detail_Id,
      dieUnloadReasonId: row.die_Unload_Reason_Id,
    };

    try {
      await inProduction.DiaUnload(payload);
      notify("Die Unloaded Successfully!", "success");

      fetchInProductionData();
      fetchDies();
    } catch (err) {
      notify("Die unload failed!", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white font-plus text-plus relative">
      {loading && <Loader centered />}
      <RegistryHeader title="Production">
        <button
          onClick={() => setIsTourOpen(true)}
          className="flex items-center gap-2 px-6 py-2 text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary/5 transition-colors border border-primary bg-white shrink-0"
          title="Start Onboarding Tour"
        >
          <Info size={14} className="shrink-0" />
          Guide
        </button>
        <div className="flex bg-gray-100 p-1 rounded-sm gap-1">
          <button
            onClick={() => setActiveTab("in-ProductionData")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "in-ProductionData"
              ? "bg-primary text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            In-Production
          </button>
          <button
            onClick={() => setActiveTab("finish")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "finish"
              ? "bg-primary text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Produced Section
          </button>
        </div>

        <div className="flex items-center gap-4">
          {activeTab === "in-ProductionData" ? (
            <AnimatePresence>
              {selectedDies.length > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={handleRefresh}
                  className="p-2 text-white bg-primary border border-primary hover:bg-[#2c5ebc] transition-all flex items-center justify-center"
                  title="Load/Refresh Selected Dies"
                  disabled={loading}
                >
                  <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
                </motion.button>
              )}
            </AnimatePresence>
          ) : (
            <button
              onClick={handleFinishRefresh}
              className="p-2 text-gray-400 hover:text-primary transition-colors border border-gray-200 hover:border-primary bg-white disabled:opacity-50"
              title="Refresh Produced Section"
              disabled={loading}
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex flex-col flex-1 overflow-hidden">
        {/* Primary Die Selection Registry - Hover Expand Logic */}
        {activeTab === "in-ProductionData" && (
          <div
            data-tour="die-registry"
            className={`mb-4 transition-all duration-500 ease-in-out border border-gray-100 bg-white overflow-hidden ${isTableExpanded
              ? "max-h-[1000px] opacity-100"
              : "max-h-12 opacity-80"
              }`}
            onMouseEnter={() => setIsTableExpanded(true)}
            onMouseLeave={() => {
              if (inProductionData.length > 0) setIsTableExpanded(false);
            }}
          >
            <div className={`overflow-auto custom-scrollbar ${isTableExpanded ? 'max-h-[300px]' : 'max-h-12'}`}>
              <Table
                columns={[
                  {
                    key: "dieNumber",
                    label: "Die No",
                    align: "center",
                    render: (val, row) => {
                      const pdfUrl = getDiePdfUrl(row);
                      return pdfUrl ? (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-black hover:underline cursor-pointer text-[11px]"
                        >
                          {val}
                        </a>
                      ) : (
                        <span className="font-black text-primary">{val}</span>
                      );
                    }
                  },
                  {
                    key: "die_Status",
                    label: "Die Status",
                    align: "center",
                    render: (val) => (
                      <span className={`px-2 py-0.5 rounded-none ${STATUS_COLORS[val?.toUpperCase()]} font-bold text-[8px] uppercase tracking-tighter`}>
                        {val || "Ready"}
                      </span>
                    )
                  },
                  {
                    key: "currentWeight",
                    label: "Current Weight",
                    align: "center",
                  },
                  { key: "cutLength", label: "Cut Length", align: "center", render: (val) => <span className="font-bold text-gray-500">{val || "—"}</span> },
                  { key: "orderPcs", label: "Order Pcs", align: "center", render: (val) => <span className="font-black text-gray-900">{val?.toLocaleString()}</span> },
                  {
                    key: "orderPcsWeight",
                    label: "Order Pcs Wt",
                    align: "center",
                    render: (val) => <span className="font-bold text-emerald-600">{val}</span>
                  },
                  { key: "soNo", label: "So No", align: "center", render: (val) => <span className="font-bold text-gray-400">{val || "—"}</span> },
                  { key: "batchId", label: "Batch Id", align: "center", render: (val) => <span className="font-mono font-bold text-gray-500">{val}</span> },
                ]}
                data={dieList}
                showActions={false}
                checkbox={{
                  idField: "die_Id",
                  selected: selectedDies,
                  onToggle: (id) => toggleDieSelection(id),
                  onToggleAll: (ids, checked) => {
                    if (checked) {
                      setSelectedDies(ids);
                    } else {
                      setSelectedDies([]);
                      setInProductionData([]);
                    }
                  }
                }}
                tableClassName="text-[11px] font-bold"
              />
            </div>
          </div>
        )}

        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >

          {activeTab === "in-ProductionData" && (
            <div className="p-4 space-y-4 bg-gray-50/30" data-tour="production-workspace">
              {inProductionData.length > 0 ? (
                inProductionData.map((row, index) => (
                  <ProductionCard
                    key={index}
                    row={row}
                    index={index}
                    handleSave={handleSave}
                    handleDieUnload={handleDieUnload}
                    handleActionChange={handleActionChange}
                    handleProdStart={handleProdStart}
                    handleRowChange={handleRowChange}
                    handleSaveBilletChange={handleSaveBilletChange}
                    calcFinal={calcFinal}
                    safeDiv={safeDiv}
                    isCastingData={isCastingData}
                    dieUnloadReason={dieUnloadReason}
                    inProductionData={inProductionData}
                    isSaving={isSaving}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-gray-300">
                  <LayoutGrid size={48} className="mb-4 opacity-5" />
                  <p className="font-black uppercase tracking-[4px] text-[10px] text-gray-400">Registry Workspace Empty</p>
                  <p className="text-[12px] mt-2 text-gray-400/60 font-medium">Select die assets from the registry above to begin production logging.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "finish" && (
            <div className="min-w-max">
              <Table
                columns={columns}
                data={isMarkDoneData}
                showActions={false}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          )}
        </div>
      </div>

      <div data-tour="calculation-footer">
        <CalculationFooter
          items={[
            {
              label: "Production Pieces",
              value: pcsTotal.toLocaleString(),
              suffix: "PCS",
              color: "text-gray-900"
            },
            {
              label: "Cumulative Weight",
              value: outputWtTotal.toLocaleString(),
              suffix: "KG",
              color: "text-primary"
            }
          ]}
        />
      </div>

      <Model
        isOpen={isBilletModelOpen}
        onClose={closeBilletModal}
        name="Execute Billet Exchange Protocol"
        widthClass="max-w-6xl"
      >
        <div className="bg-white border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4 border-b border-gray-100 pb-2">
            <span className="w-2 h-2 bg-amber-500"></span>
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Finalizing Current Cycle</h3>
          </div>
          {billetModalRow?.old && (
            <ProductionCard
              row={billetModalRow.old}
              index={billetModalRow.index}
              isModal={true}
              handleRowChange={() => { }}
              isSaving={isSaving}
              calcFinal={calcFinal}
              safeDiv={safeDiv}
              isCastingData={isCastingData}
            />
          )}
        </div>

        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4 border-b border-gray-100 pb-2">
            <span className="w-2 h-2 bg-green-500"></span>
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Initiating Next Cycle</h3>
          </div>
          {billetModalRow?.new && (
            <ProductionCard
              row={billetModalRow.new}
              index={billetModalRow.index}
              isModal={true}
              handleRowChange={handleModalRowChange}
              handleSaveBilletChange={handleSaveBilletChange}
              isSaving={isSaving}
              calcFinal={calcFinal}
              safeDiv={safeDiv}
              isCastingData={isCastingData}
            />
          )}
        </div>
      </Model>

      <TourGuide
        isOpen={isTourOpen}
        steps={InProductionTourSteps}
        title="In-Production Guide"
        model="In-Production Module"
        onClose={() => {
          setIsTourOpen(false);
          handleTourComplete();
        }}
      />
    </div>

  );
};

export default InProduction;
